/**
 * The receiving side of a mirrored screen (the console's Screen view and the phone's), AIOE's own: the parts of each
 * frame (`MirrorFramePart`, the workbench's schemas/mirror.ts) are joined in order, a newer frame replaces an
 * unfinished older one, and the bytes each frame used are reported so the receiver grants that credit back once the
 * frame is drawn (which is what sets the rate: the workbench sends the next frame only within the credit). Also what
 * a screen channel's close means, from the relay's refusals (AIOE's) and the workbench's (`MIRROR_ERRORS`). No DOM or
 * React Native here, so both apps and node:test use it.
 */
import { MIRROR_ERRORS, mirrorFramePartSchema } from "../schemas/mirror.js";
/**
 * Why the relay refused a screen's open (the `closed` reason starts with one of these): the platform's own rules,
 * before the workbench hears of it (packages/api/src/relay/mirror.ts). The workbench's are `MIRROR_ERRORS`.
 */
export const MIRROR_RELAY_ERRORS = {
    /** The machine was not granted ui:view, or ui:control to drive. */
    scope: "mirror_scope",
    /** The organisation has as many screens open as the platform allows. */
    limit: "mirror_limit",
    /** Mojo Up AI Cloud: the organisation is not on Teams. */
    teamsRequired: "teams_required",
    /** Driving needs a recent sign-in. */
    signInAgain: "sign_in_again",
};
/** The most parts one frame may claim (about 9 MB of JPEG); a part claiming more is ignored. */
const MAX_PARTS = 256;
export class MirrorFrameAssembler {
    current;
    bytes = 0;
    last = -1;
    /**
     * Take one channel `data` payload and the bytes the relay counted for it. Returns the frame when this part
     * completed one; undefined while it waits for more, or for a payload that is not a part of a newer frame (an
     * unknown `kind` is ignored, as the contract asks).
     */
    push(data, bytes) {
        this.bytes += Math.max(0, bytes);
        const p = mirrorFramePartSchema.safeParse(data);
        if (!p.success)
            return undefined;
        const f = p.data;
        if (f.frame <= this.last || f.part >= f.parts || f.parts > MAX_PARTS)
            return undefined;
        if (!this.current || this.current.frame !== f.frame) {
            // A newer frame starts before the older one finished: the older one is dropped.
            if (this.current && f.frame < this.current.frame)
                return undefined;
            this.current = { frame: f.frame, parts: new Array(f.parts), got: 0, width: f.width, height: f.height };
        }
        const c = this.current;
        if (c.parts.length !== f.parts || c.parts[f.part] !== undefined)
            return undefined;
        c.parts[f.part] = f.b64;
        c.got++;
        if (c.got < c.parts.length)
            return undefined;
        this.current = undefined;
        this.last = c.frame;
        const ready = { frame: c.frame, width: c.width, height: c.height, b64: c.parts.join(""), bytes: this.bytes };
        this.bytes = 0;
        return ready;
    }
    /** Bytes taken but not yet reported with a frame. */
    pending() {
        return this.bytes;
    }
    /**
     * Hand over the bytes not yet reported, to grant back now: a frame larger than half the channel's credit would
     * otherwise wait for credit that is only granted once it is drawn.
     */
    drain() {
        const n = this.bytes;
        this.bytes = 0;
        return n;
    }
}
/** `mirror_unavailable` covers two cases the workbench names in words: a node has no window at all; a desktop's is closed right now. */
function windowKind(reason) {
    if (/has no window to mirror/i.test(reason ?? ""))
        return "no_screen";
    if (/no window to mirror right now/i.test(reason ?? ""))
        return "window_closed";
    return undefined;
}
/** The kind of a screen channel's close, from its code and the `"<error>: <message>"` reason. `driving`: it asked for input. */
export function mirrorCloseKind(code, reason, driving = false) {
    const error = /^([a-z_]+):/.exec(reason ?? "")?.[1];
    switch (error) {
        case MIRROR_RELAY_ERRORS.scope:
            return /control/i.test(reason ?? "") ? "control_not_granted" : "not_granted";
        case MIRROR_RELAY_ERRORS.signInAgain:
            return "sign_in_again";
        case MIRROR_RELAY_ERRORS.limit:
            return "limit";
        case MIRROR_RELAY_ERRORS.teamsRequired:
            return "teams_required";
        case MIRROR_ERRORS.off:
            return "off";
        case MIRROR_ERRORS.unavailable:
            return windowKind(reason) ?? "unavailable";
        case "method_not_found":
            return "needs_update";
    }
    // A workbench from before mirror.subscribe refuses it in its own words.
    if (/not a channel|method not found|unknown method/i.test(reason ?? ""))
        return "needs_update";
    const window = windowKind(reason);
    if (window)
        return window;
    switch (code) {
        case "done":
            return "off";
        case "scope":
            return driving ? "control_withdrawn" : "not_granted";
        case "revoked":
            // The workbench tells its two reasons apart in words only (controlGateway remoteOffReason).
            if (/kill switch/i.test(reason ?? ""))
                return "kill_switch";
            if (/not allowed by/i.test(reason ?? ""))
                return "policy_off";
            return reason && /access|member|token/i.test(reason) ? "ended" : "kill_switch";
        case "workbench_offline":
        case "workbench_closed":
            return "offline";
        default:
            return "ended";
    }
}
//# sourceMappingURL=mirrorFrames.js.map